import "./autoPrint.scss";
import { useMemo } from "react";
import { useOutletContext } from "react-router-dom";

const AutoPrint = () => {
    const { autoPrintConfigured, setAutoPrintConfigured } = useOutletContext();

    const ordersUrl = useMemo(() => {
        return new URL("/orders", window.location.origin).toString();
    }, []);

    const windowsChromeCommand = useMemo(() => {
        return (
            '"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe" --kiosk --kiosk-printing "' +
            ordersUrl +
            '"'
        );
    }, [ordersUrl]);

    const macChromeCommand = useMemo(() => {
        return `open -na "Google Chrome" --args --kiosk --kiosk-printing "${ordersUrl}"`;
    }, [ordersUrl]);

    return (
        <div className="auto-print-page">
            <div className="auto-print-page__hero">
                <div>
                    <p className="auto-print-page__eyebrow">الطباعة التلقائية</p>
                    <h1>إعداد الطباعة التلقائية على جهاز المطعم</h1>
                    <p className="auto-print-page__lead">
                        هذه الصفحة تشرح لك كيف تجعل الطلبات الجديدة تُطبع تلقائيًا على هذا الجهاز.
                        بعد إنهاء الخطوات على هذا المتصفح، فعّل الطباعة التلقائية من الزر في الأسفل
                        حتى لا يحاول النظام الطباعة على الأجهزة غير المجهزة.
                    </p>
                </div>
            </div>

            <div className="auto-print-page__grid">
                <section className="auto-print-card">
                    <h2>قبل البدء</h2>
                    <ul>
                        <li>استخدم جهازًا ثابتًا داخل المطعم لاستقبال الطلبات.</li>
                        <li>وصّل الطابعة بهذا الجهاز وتأكد أنها تطبع بشكل طبيعي.</li>
                        <li>اجعل الطابعة هي الطابعة الافتراضية في الجهاز.</li>
                        <li>استخدم متصفح Google Chrome على نفس الجهاز.</li>
                    </ul>
                </section>

                <section className="auto-print-card">
                    <h2>الخطوات العامة</h2>
                    <ol>
                        <li>أغلق Chrome بالكامل قبل بدء الإعداد.</li>
                        <li>جهّز Chrome ليعمل بوضع الكشك والطباعة الصامتة معًا.</li>
                        <li>افتح لوحة الطلبات من هذا الجهاز فقط.</li>
                        <li>اترك صفحة الطلبات مفتوحة طوال وقت العمل.</li>
                        <li>ارجع إلى هذه الصفحة واضغط زر تفعيل الطباعة التلقائية.</li>
                    </ol>
                </section>

                <section className="auto-print-card auto-print-card--full">
                    <h2>ما الفرق بين الخيارين؟</h2>
                    <ul>
                        <li>
                            <code>--kiosk</code> يفتح Chrome بواجهة مخصصة وبملء الشاشة.
                        </li>
                        <li>
                            <code>--kiosk-printing</code> يجعل Chrome يوافق على الطباعة تلقائيًا عند
                            وصول أمر الطباعة.
                        </li>
                        <li>
                            الأفضل أن تستخدم <code>--kiosk</code> و <code>--kiosk-printing</code>
                            معًا حتى تكون الصفحة مخصصة للطباعة والطلب يخرج تلقائيًا.
                        </li>
                    </ul>
                </section>

                <section className="auto-print-card auto-print-card--full">
                    <h2>طريقة الإعداد على Windows</h2>
                    <ol>
                        <li>على سطح المكتب اضغط بزر الفأرة الأيمن على اختصار Chrome.</li>
                        <li>
                            اختر <span>Properties</span>.
                        </li>
                        <li>
                            في خانة <span>Target</span> اترك المسار كما هو، ثم أضف في النهاية:
                            <code>--kiosk --kiosk-printing</code>
                        </li>
                        <li>
                            إذا أردت فتح شاشة الطلبات مباشرة، اجعل السطر بهذا الشكل:
                            <code>{windowsChromeCommand}</code>
                        </li>
                        <li>
                            اضغط <span>Apply</span> ثم <span>OK</span>.
                        </li>
                        <li>بعد ذلك افتح Chrome دائمًا من هذا الاختصار فقط.</li>
                    </ol>
                </section>

                <section className="auto-print-card auto-print-card--full">
                    <h2>طريقة الإعداد على Mac</h2>
                    <ol>
                        <li>
                            افتح تطبيق <span>Terminal</span>.
                        </li>
                        <li>
                            شغّل Chrome بهذا الأمر:
                            <code>{macChromeCommand}</code>
                        </li>
                        <li>إذا نجحت الطريقة احفظ الأمر عندك لاستخدامه كل يوم.</li>
                        <li>بعد فتح Chrome اترك صفحة الطلبات مفتوحة أثناء العمل.</li>
                    </ol>
                </section>

                <section className="auto-print-card">
                    <h2>إذا لم تتم الطباعة</h2>
                    <ul>
                        <li>تأكد أن الطابعة شغالة وبها ورق.</li>
                        <li>تأكد أن الطابعة الافتراضية لم تتغير.</li>
                        <li>تأكد أنك فتحت Chrome من الاختصار المعدّل أو من أمر Terminal الصحيح.</li>
                        <li>تأكد أن صفحة الطلبات ما زالت مفتوحة على هذا الجهاز.</li>
                    </ul>
                </section>

                <section className="auto-print-card">
                    <h2>تفعيل الطباعة على هذا الجهاز</h2>
                    <p>
                        بعد الانتهاء من الإعداد على هذا الجهاز، فعّل الطباعة التلقائية من هنا. هذا
                        التفعيل يخص هذا المتصفح وهذا الجهاز فقط.
                    </p>

                    <div className="auto-print-card__actions">
                        <button
                            className={`auto-print-card__button ${
                                autoPrintConfigured ? "is-active" : ""
                            }`}
                            onClick={() => setAutoPrintConfigured(true)}
                        >
                            تم إعداد هذا الجهاز للطباعة التلقائية
                        </button>
                        <button
                            className="auto-print-card__button is-secondary"
                            onClick={() => setAutoPrintConfigured(false)}
                        >
                            إلغاء التفعيل على هذا الجهاز
                        </button>
                    </div>

                    <ul>
                        <li>إذا لم تكن الطباعة الصامتة مجهزة، لا تقم بتفعيل هذا الزر.</li>
                        <li>إذا فعّلت الطباعة التلقائية على جهاز عادي فقد تظهر نافذة الطباعة.</li>
                        <li>يمكنك إلغاء التفعيل في أي وقت من نفس الصفحة.</li>
                    </ul>
                </section>
            </div>
        </div>
    );
};

export default AutoPrint;
